<template>
  <v-card
    id="import-modal-list-card"
    class="pb-1"
    :color="$vuetify.theme.dark ? 'dark_backgroundDarkBlue' : undefined"
  >
    <v-card-title>
      {{ $t(`views.imports.importModal.importModalListCard.title.${type}`) }}
    </v-card-title>

    <v-card-text class="py-0 mb-0 mt-5">
      <v-data-table
        id="import-modal-list-card-table"
        :headers="headers"
        :items="imports"
        class="elevation-0"
        mobile-breakpoint="0"
        :loading="!loadedData"
        :items-per-page="10"
        :dense="user.preferences.denseTables"
      >
        <template v-slot:item.createdAt="{ item }">
          {{ getDateUserFullFormat(item.createdAt) }}
        </template>
        <template v-slot:item.broker.alias="{ item }">
          <TableBroker v-if="item.broker" :broker="item.broker" />
          <span v-else></span>
        </template>
        <template v-slot:item.type="{ item }">
          <span v-if="item.type === 'orderGeneric' || item.type === 'dividendGeneric'">
            {{ $t(`views.imports.importModal.importModalUploadCard.generic.title`) }}
          </span>
          <span v-else>
            {{ $t(`views.imports.importModal.importModalUploadCard.custom.title`) }}
          </span>
        </template>
        <template v-slot:item.startEnd="{ item }">
          <div
            v-if="
              getDatesFromImport(item) &&
              getDatesFromImport(item).firstSuccessElementDate &&
              getDatesFromImport(item).lastSuccessElementDate
            "
          >
            {{
              getDateToFormat({
                date: getDatesFromImport(item).firstSuccessElementDate,
                format: 'dd/MM/yyyy',
              })
            }}
            -
            {{
              getDateToFormat({
                date: getDatesFromImport(item).lastSuccessElementDate,
                format: 'dd/MM/yyyy',
              })
            }}
          </div>
          <span v-else></span>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip small :color="getStatusColor(item.status)">
            {{ $t(`views.imports.importModal.importModalListCard.status.${item.status}`) }}
          </v-chip>
        </template>
        <template v-slot:item.action="{ item }">
          <template v-if="item.status === 'requiresDict'">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" class="mr-2" v-on="on" @click="cancelImport(item)">
                  mdi-close-box-outline
                </v-icon>
              </template>
              <span>{{ $t(`views.imports.importModal.importModalListCard.cancel`) }}</span>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  class="import-modal-list-card-table-complete-icon"
                  v-on="on"
                  @click="goToImport(item)"
                >
                  mdi-order-bool-ascending-variant
                </v-icon>
              </template>
              <span>{{ $t(`views.imports.importModal.importModalListCard.complete`) }}</span>
            </v-tooltip>
          </template>
          <template v-if="loadedData && item.status === 'processed'">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" @click="undoImport(item)">
                  mdi-file-undo-outline
                </v-icon>
              </template>
              <span>{{ $t(`views.imports.importModal.importModalListCard.undo.action`) }}</span>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  class="ml-1 import-modal-list-card-table-show-result-icon"
                  size="23"
                  v-on="on"
                  @click="goToImport(item)"
                >
                  mdi-view-list
                </v-icon>
              </template>
              <span>{{ $t(`views.imports.importModal.importModalListCard.showResult`) }}</span>
            </v-tooltip>
          </template>
        </template>
      </v-data-table>
    </v-card-text>
    <v-card-actions class="mt-3">
      <a class="import-mode-link ml-5" @click="$emit('goImportMode')">
        {{ $t('views.imports.importModal.importMode') }}
      </a>
      <v-spacer />
      <v-btn
        class="mr-1"
        text
        :disabled="!loadedData"
        :small="$vuetify.breakpoint.smAndDown"
        @click="$emit('close')"
      >
        {{ $t('common.close') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { get, map, sortBy } from 'lodash'
import { mapState } from 'vuex'
import { getImports, undoImport, cancelImport } from '@/lib/api.js'
import { getDateUserFullFormat, getDateToFormat } from '@/lib/utils/date.js'
import gtag from '@/lib/gtag'
import TableBroker from '@/views/components/common/TableBroker'

export default {
  components: { TableBroker },
  props: {
    type: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    imports: [],
    loadedData: false,
    dateType: 'month',
  }),
  computed: {
    ...mapState(['user']),
    headers() {
      return [
        {
          text: this.$t('views.imports.importModal.importModalListCard.tableHeaders.actions'),
          value: 'action',
        },
        {
          text: this.$t('views.imports.importModal.importModalListCard.tableHeaders.createdAt'),
          value: 'createdAt',
        },
        {
          text: this.$t('views.imports.importModal.importModalListCard.tableHeaders.status'),
          value: 'status',
        },
        {
          text: this.$t('views.imports.importModal.importModalListCard.tableHeaders.broker'),
          value: 'broker.alias',
        },
        // {
        //   text: this.$t('views.imports.importModal.importModalListCard.tableHeaders.method'),
        //   value: 'method',
        // },
        {
          text: this.$t('views.imports.importModal.importModalListCard.tableHeaders.type'),
          value: 'type',
        },
        {
          text: this.$t('views.imports.importModal.importModalListCard.tableHeaders.startEnd'),
          value: 'startEnd',
        },
      ]
    },
  },
  created() {
    this.loadImports()

    gtag.pageview({
      pagePath: `/input/${this.type === 'order' ? 'orders' : 'dividends'}/import/list`,
      pageTitle: `input|${this.type === 'order' ? 'orders' : 'dividends'}|import|list`,
      pageContentGroup: 'modal',
    })
  },
  methods: {
    getDateUserFullFormat,
    getDateToFormat,
    async loadImports() {
      this.loadedData = false
      try {
        const result = await getImports({
          filter: {
            status: { $nin: ['errored', 'undone', 'cancelled'] },
            type:
              this.type === 'order' ? ['order', 'orderGeneric'] : ['dividend', 'dividendGeneric'],
          },
        })
        this.imports = result.data
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }

      this.loadedData = true
    },
    getStatusColor(status) {
      const colors = {
        pending: 'softAdvise',
        requiresDict: this.$vuetify.theme.dark ? 'dark_backgroundSoftBlue' : 'softWarning',
        processing: 'softInfo',
        processed: 'rgb(26, 233, 172, 0.5)',
        errored: 'softError',
        undone: 'darkBackgroundGrey',
      }
      return get(colors, status, '')
    },
    getDatesFromImport(item) {
      let firstSuccessElementDate, lastSuccessElementDate
      if (item.result && item.result.successElements && item.result.successElements.length) {
        const dates = sortBy(map(item.result.successElements, 'date'))
        firstSuccessElementDate = dates[0]
        lastSuccessElementDate = dates[dates.length - 1]
      }
      return { firstSuccessElementDate, lastSuccessElementDate }
    },
    goToImport(myImport) {
      this.$emit('goToImport', myImport)
    },
    async undoImport(myImport) {
      if (
        await this.$root.$confirm({
          title: this.$t('views.imports.importModal.importModalListCard.undo.confirm.title'),
          message: this.$t('views.imports.importModal.importModalListCard.undo.confirm.message'),
        })
      ) {
        try {
          this.loadedData = false
          await undoImport({
            _id: myImport._id,
          })
          this.loadImports()
        } catch (error) {
          this.$root.$snackbar.error({ text: this.$t('common.commonError') })
          this.loadedData = true
        }
      }
    },
    async cancelImport(myImport) {
      try {
        this.loadedData = false
        await cancelImport({
          _id: myImport._id,
        })
        this.loadImports()
      } catch (error) {
        this.$root.$snackbar.error({ text: this.$t('common.commonError') })
        this.loadedData = true
      }
    },
  },
}
</script>

<style lang="scss">
#import-modal-list-card {
  #import-modal-list-card-table {
    .import-modal-list-card-table-show-result-icon {
      margin-bottom: 1px;
    }
  }
  #import-modal-list-card-table.theme--dark {
    background-color: #323940;
  }

  .import-mode-link {
    font-size: 15px;
    font-weight: 500;
  }
}
</style>
